import * as React from 'react';
import { SafeForm, DataEntryModeToggle } from './Components';
import { SafePageState } from './Types';
import { IPackIdentifier, PackIdentifier, scheme } from '../../../../Types/PackIdentifier';
import { DashboardPage, ActionButton, RagBadge, SinglePackResponse } from '../../Components';
import 'Utils/Extensions';
import { BarcodeScannerHandler } from '../../../../Services/BarcodeScannerHandler';
import { EventHandlerProvider } from '../../../../Services/EventHandlerService';
import { NeedToCalibrateScannerWarningEvent, ActionCannotBeUndone, VeriLiteWarningEvent } from '../../../../Types';
import History from '../../../../Utils/History';
import { postJson, UserRequestHeaders } from '../../../../Services/Util';
import { Badge, Row, Col } from 'reactstrap';
import './Styles/Safe.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTimesCircle, faCheckCircle, faQuestion, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { IAuthenticatedAppData } from '../../../../AuthenticatedApp/AuthenticatedApp';
import { Auth } from '../../../../Services/AuthenticationService';
import { SinglePackRequestService } from '../../../../Services/SinglePackRequestService';
import { PackAction } from '../../Types';
import * as SignalR from "@microsoft/signalr";


interface ISafeProps {
    applicationData: IAuthenticatedAppData;
}

interface ISafeState {
    manualEntry: boolean;
    availableActions: PackAction[];
    currentState: SafePageState;
    packIdentifier: IPackIdentifier;
    result: any;
    selectedAction: PackAction;
    clickDisabled: boolean;
}

export class Safe extends React.Component<ISafeProps, ISafeState> {
    barcodeScannerHandler: any;
    scannedData: any;

    constructor(props: ISafeProps) {
        super(props);

        if (this.props.applicationData.userInfo.UserRole.match(/SuperUser/g)) {
            History.push('/Dashboard/Reports');
        }

        this.barcodeScannerHandler = new BarcodeScannerHandler(this.parseScannerInput.bind(this));
        this.scannedData = {};
        this.state = {
            manualEntry: this.props.applicationData.apiProvider == "Ssr" && this.props.applicationData.calibrationData == null,
            availableActions: [],
            currentState: SafePageState.Waiting,
            packIdentifier: new PackIdentifier(),
            result: {},
            selectedAction: PackAction.Verify,
            clickDisabled: false
        };

        document.addEventListener("keypress",
            this.barcodeScannerHandler.keyPressHandler,
            true);

        document.addEventListener("keydown",
            this.barcodeScannerHandler.keyDownHandler,
            true);

        document.addEventListener("keyup",
            this.barcodeScannerHandler.keyUpHandler,
            true);
    }

    componentWillUnmount() {
        document.removeEventListener("keypress",
            this.barcodeScannerHandler.keyPressHandler,
            true);

        document.removeEventListener("keydown",
            this.barcodeScannerHandler.keyDownHandler,
            true);

        document.removeEventListener("keyup",
            this.barcodeScannerHandler.keyUpHandler,
            true);
    }

    componentDidUpdate(prevProps: ISafeProps) {
        if (prevProps.applicationData.selectedLocation != this.props.applicationData.selectedLocation) {
            this.setState({ manualEntry: this.props.applicationData.apiProvider == "Ssr" && this.props.applicationData.calibrationData == null });
        }
    }

    parseScannerInput(dom3KeyboardEventCodeSupport: any) {
        if (this.props.applicationData.connectionState != SignalR.HubConnectionState.Connected) {
            EventHandlerProvider.GetInstance().AddEvent(new VeriLiteWarningEvent(this.props.applicationData.resources.VeriLiteDisconnected));
            return;
        }

        if (this.state.currentState === SafePageState.Scanning || this.state.currentState === SafePageState.Typing || this.state.currentState === SafePageState.Sending) {
            return;
        }

        if (dom3KeyboardEventCodeSupport) {
            this.scannedData = this.barcodeScannerHandler.scannerInput.getInput();
        } else {
            this.scannedData = this.barcodeScannerHandler.scannerInput.getInputBytes();
        }
        let scannedString = String.fromCharCode(...this.scannedData);
        if (this.scannedData.length > 20) {
            if (this.props.applicationData.calibrationData == null) {
                EventHandlerProvider.GetInstance().AddEvent(NeedToCalibrateScannerWarningEvent(this.props.applicationData.resources));
                return;
            }

            this.setState({
                currentState: SafePageState.Scanning,
                manualEntry: false,
                packIdentifier: new PackIdentifier(),
                availableActions: [],
                selectedAction: PackAction.Verify
            }, () => {
                this.parsePackData(JSON.stringify({ ScannedData: scannedString, CalibrationData: this.props.applicationData.calibrationData }));
            });
        }
    }

    parsePackData = (json: string) => {
        const headers = new UserRequestHeaders( { Organisation: Auth.getUser().Organisation, Location: Auth.getUser().Location, id: Auth.getUser().id });
        postJson(`/Parser/Parse`, json, undefined, undefined,  headers)
            .then((result: IPackIdentifier) => {
                if (result.submit) {
                    this.setState({
                        packIdentifier: result,
                        currentState: SafePageState.Sending
                    }, () => {
                        SinglePackRequestService.SubmitPackRequest(result, PackAction.Verify, false, this.props.applicationData.selectedLocation, this.props.applicationData.resources).then((singlePackResult) => {
                            this.setState({
                                result: singlePackResult,
                                currentState: SafePageState.Result,
                                availableActions: singlePackResult.packActions
                            });
                        }).catch((err) => {
                            console.error(err);
                            this.setState({
                                currentState: SafePageState.Error,
                                availableActions: []
                            });
                        });
                    });
                }
                else {
                    console.error("Display error");
                    this.setState({
                        result: {
                            packInfo: {
                                Warning: this.props.applicationData.resources.CannotParseBarcode
                            },
                            ragClass: "Red"
                        },
                        currentState: SafePageState.Result,
                        availableActions: []
                    });
                }
            });
    };
    
    actionButtonOnClick = (action: PackAction) => {
        if (this.state.currentState === SafePageState.Scanning || this.state.currentState === SafePageState.Sending) {
            return;
        }

        if (action.value == PackAction.MarkAsDestroyed.value || action.value == PackAction.MarkAsStolen.value) {
            EventHandlerProvider.GetInstance().AddEvent(
                ActionCannotBeUndone(
                    this.props.applicationData.resources,
                    () => {
                        this.setState(
                            {
                                selectedAction: action,
                                currentState: SafePageState.Sending
                            },
                            () => {
                                SinglePackRequestService.SubmitPackRequest(this.state.packIdentifier, this.state.selectedAction, this.state.manualEntry, this.props.applicationData.selectedLocation, this.props.applicationData.resources)
                                    .then((singlePackResult) => {
                                        this.setState({
                                            result: singlePackResult,
                                            currentState: SafePageState.Result,
                                            availableActions: singlePackResult.packActions,
                                            clickDisabled: false
                                        });
                                    })
                                    .catch((err) => {
                                        console.error(err);
                                        this.setState({
                                            currentState: SafePageState.Error,
                                            availableActions: [],
                                            clickDisabled: false
                                        });
                                    });
                            }
                        );
                    })
            );
        } else {
            this.setState(
                {
                    selectedAction: action,
                    currentState: SafePageState.Sending
                },
                () => {
                    SinglePackRequestService.SubmitPackRequest(this.state.packIdentifier, this.state.selectedAction, this.state.manualEntry, this.props.applicationData.selectedLocation, this.props.applicationData.resources, this.state.result)
                        .then((singlePackResult) => {
                            this.setState({
                                result: singlePackResult,
                                currentState: SafePageState.Result,
                                availableActions: singlePackResult.packActions,
                                clickDisabled: false
                            });
                        })
                        .catch((err) => {
                            console.error(err);
                            this.setState({
                                currentState: SafePageState.Error,
                                availableActions: [],  
                                clickDisabled: false
                            });
                        });
                }
            );
        }
    };

    manualInputOnChangeHandler = (e: React.BaseSyntheticEvent) => {
        //console.log("manualInputOnChangeHandler");
        //console.log(`Updated ${e.currentTarget.name}: ${e.currentTarget.value}`);
        let newAvailableActions = this.state.availableActions;
        let newPackIdentifier = this.state.packIdentifier;
        newPackIdentifier[e.currentTarget.name as string] = e.currentTarget.value;

        //console.log("newPackIdentifier");
        //console.log(newPackIdentifier);
        if (!String.isNullOrWhitespace(newPackIdentifier.productCode) && !String.isNullOrWhitespace(newPackIdentifier.serialNumber)) {
            //console.log("Product Code and Serial Number are not null or whitepsace");
            if (Array.isNullOrEmpty(newAvailableActions)) {
                //console.log("avaiableActions is null or empty");
                newAvailableActions = [PackAction.Verify];
            }
        } else {
            //console.log("Product Code and Serial Number are null or whitepsace");
            newAvailableActions = [];
        }

        newPackIdentifier.scheme = newPackIdentifier.productCode.length == 14 ? scheme.Gs1 : scheme.Ifa;
        this.setState({
            currentState: SafePageState.Typing,
            packIdentifier: newPackIdentifier,
            availableActions: newAvailableActions
        });
    };

    dataEntryModeToggleHandler = () => {
        if (this.props.applicationData.calibrationData == null) {
            EventHandlerProvider.GetInstance().AddEvent(NeedToCalibrateScannerWarningEvent(this.props.applicationData.resources));
            return;
        }

        var manualEntry = this.props.applicationData.apiProvider == "Ssr" ? !this.state.manualEntry : false;

        this.setState({
            manualEntry: manualEntry,
            currentState: SafePageState.Waiting,
            packIdentifier: new PackIdentifier(),
            availableActions: []
        });
    };

    render() {
        return (
            <DashboardPage
                content={
                    <div>
                        {this.props.applicationData.apiProvider == "Ssr" && <DataEntryModeToggle onChange={this.dataEntryModeToggleHandler} manualEntry={this.state.manualEntry} resources={this.props.applicationData.resources} />}
                        <SafeForm manualEntry={this.state.manualEntry} onChangeHandler={this.manualInputOnChangeHandler} currentState={this.state.currentState} packIdentifier={this.state.packIdentifier} resources={this.props.applicationData.resources} />
                        {this.state.currentState === SafePageState.Result &&
                            <SinglePackResponse result={this.state.result} resources={this.props.applicationData.resources} showHelpLink={this.props.applicationData.showHelpLink} packIdentifier={this.state.packIdentifier} />
                        }
                    </div>
                }
                actions={!Array.isNullOrEmpty(this.state.availableActions) &&
                    <div>
                        {this.state.availableActions.map((action: PackAction) =>
                            <ActionButton
                                key={action.value}
                                action={action}
                                onClick={() => {                                 
                                    if (this.state.clickDisabled) return;
                                    this.setState({ clickDisabled: true });
                                    this.actionButtonOnClick(action); 
                                }}
                                active={this.state.selectedAction == action}
                                disabled={this.state.clickDisabled} />
                        )}
                    </div>
                }
                placeholder={this.props.applicationData.resources.ActionsWillAppear_Single}
                title={this.props.applicationData.resources.PackDetails}
                resources={this.props.applicationData.resources}
            />
        );
    }
}